import * as React from 'react'

import { Box, Button, Icon, Text, Touchable } from '@factorialco/deprecated-design-system'
import {
  HiringStagePhaseFragment,
  UpdatePositionsAtsHiringPhaseNewPositionsInput,
} from 'generated/resources/graphql'
import orderBy from 'lodash/orderBy'
import { useToggle } from 'usehooks-ts'

import SortableBoxList from 'modules/core/components/SortableBoxList'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { HiringStage } from 'modules/ats/hooks/hiringStages/useHiringStages'
import { useSwapHiringStagePositions } from 'modules/ats/hooks/hiringStages/useSwapHiringStagePositions'
import { useUpdateHiringPhasesPosition } from 'modules/ats/hooks/hiringStages/useUpdateHiringPhasesPosition'
import { getProperties } from 'modules/ats/lib/utils/hiringStages'

import { AddPhaseToStageModal } from './AddPhaseToStageModal'
import { DeletePhaseModal } from './DeletePhaseModal'
import { DeleteStageModal } from './DeleteStageModal'
import { EditHiringPhaseModal } from './EditHiringPhaseModal'
import { StagePhaseItem } from './StagePhaseItem'

interface Props {
  stage: HiringStage
  stages: HiringStage[]
  previousStage: HiringStage | undefined
  nextStage: HiringStage | undefined
}

export const EditableStage = ({ stage, stages, nextStage, previousStage }: Props) => {
  const [showAddPhase, setShowAddPhase] = React.useState(false)
  const [selectedPhase, setSelectedPhase] = React.useState<HiringStagePhaseFragment | null>(null)
  const [showEditPhaseModal, toggleEditPhaseModal] = useToggle(false)
  const [showDeletePhaseModal, toggleDeletePhaseModal] = useToggle(false)
  const [showDeleteStageModal, toggleDeleteStageModal] = useToggle(false)
  const { updatePositions } = useUpdateHiringPhasesPosition()
  const { swapPositionsAtsHiringStage } = useSwapHiringStagePositions()

  const onEditPhase = (hiringPhase: HiringStagePhaseFragment) => {
    setSelectedPhase(hiringPhase)
    toggleEditPhaseModal()
  }

  const onDeletePhase = (hiringPhase: HiringStagePhaseFragment) => {
    setSelectedPhase(hiringPhase)
    toggleDeletePhaseModal()
  }

  const setFlashMessage = (success: boolean) => {
    const type = success ? 'correct' : 'wrong'
    const message = success
      ? i18n.t('common.saved_successfully')
      : i18n.t('ats.hiring_pipelines.settings.sidepanel.change_position_error')

    app.setFlash({
      type: type,
      message: message,
      time: 2000,
    })
  }

  const onSortPhases = async (reorderedItems: HiringStagePhaseFragment[]) => {
    const sortedItems = reorderedItems.map((item, index) => {
      const stageItem = stage.hiringPhases.find((e) => e.id === item.id)
      if (stageItem) {
        return {
          atsHiringPhaseId: stageItem.id,
          newPosition: index,
        }
      }

      return stageItem
    })

    const items = sortedItems.filter((e) => e !== undefined) as [
      UpdatePositionsAtsHiringPhaseNewPositionsInput,
    ]

    const result = await updatePositions(items)

    const handled = handleResult(result, (_errors) => {
      // TODO remove it when handle result does not need error function
    })

    setFlashMessage(handled.success)
  }

  const sortedStagePhases = orderBy(stage.hiringPhases, (phase) => phase.position)

  const onSwapHiringStage = async (
    hiringStageIdOne: number | undefined,
    hiringStageIdTwo: number | undefined
  ) => {
    if (hiringStageIdOne && hiringStageIdTwo) {
      const result = await swapPositionsAtsHiringStage(hiringStageIdOne, hiringStageIdTwo)
      const handled = handleResult(result, (_errors) => undefined)

      setFlashMessage(handled.success)
    }
  }

  return (
    <>
      <Box
        borderRadius={{ bottomLeft: 'abs008', bottomRight: 'abs008' }}
        padding="s16"
        gap="s16"
        border={{
          all: { color: 'grey400', style: 'solid', width: 's1' },
          top: { color: getProperties(stage.name).color, style: 'solid', width: 's4' },
        }}
      >
        <Box flexDirection="row" width="full" justifyContent="spaceBetween" alignItems="center">
          <Box flexDirection="row" gap="s8">
            <Icon.Medium icon={getProperties(stage.name).icon} color="grey800" />
            <Text.Normal weight="medium" color="grey800">
              {stage.label}
            </Text.Normal>
          </Box>
          <Box flexDirection="row" gap="s4">
            {stage.position > 1 && previousStage && (
              <Button
                type="secondary"
                onClick={() => onSwapHiringStage(stage.id, previousStage.id)}
                iconName="chevron-up"
              />
            )}
            {nextStage && (
              <Button
                type="secondary"
                onClick={() => onSwapHiringStage(stage.id, nextStage.id)}
                iconName="chevron-down"
              />
            )}
            <Button type="secondary" onClick={toggleDeleteStageModal} iconName="delete" />
          </Box>
        </Box>

        <SortableBoxList
          items={sortedStagePhases}
          emptyMessage={i18n.t('ats.job_details.hiring_process.list.empty.application_phases')}
          onSortEnd={onSortPhases}
          itemAsKey={(item) => item.id.toString()}
          renderItem={(item) => (
            <StagePhaseItem
              item={item}
              onEdit={() => onEditPhase(item)}
              onDelete={() => onDeletePhase(item)}
            />
          )}
        />

        <Touchable onClick={() => setShowAddPhase(true)}>
          <Box flexDirection="row" alignItems="center">
            <Icon.Small icon="add" color="viridian1200" />
            <Text.Normal color="viridian1200" size="200" weight="medium">
              {i18n.t('ats.hiring_pipelines.settings.sidepanel.new_phase')}
            </Text.Normal>
          </Box>
        </Touchable>
      </Box>
      {showAddPhase && (
        <AddPhaseToStageModal
          currentStage={stage}
          editableStages={stages}
          onClose={() => setShowAddPhase(false)}
        />
      )}

      {showDeleteStageModal && (
        <DeleteStageModal currentStage={stage} onClose={toggleDeleteStageModal} />
      )}

      {showDeletePhaseModal && selectedPhase && (
        <DeletePhaseModal currentPhaseId={selectedPhase.id} onClose={toggleDeletePhaseModal} />
      )}

      {showEditPhaseModal && selectedPhase && (
        <EditHiringPhaseModal
          atsHiringPhase={selectedPhase}
          stages={stages}
          onClose={toggleEditPhaseModal}
        />
      )}
    </>
  )
}
